import React from 'react';
import { useNotification } from '../contexts/NotificationContext';
import {
  CheckCircle2,
  AlertCircle,
  Info,
  AlertTriangle,
  X
} from 'lucide-react';

const NotificationItem = ({ id, message, type, duration, onRemove }) => {
  const icons = {
    success: <CheckCircle2 className="text-green-500 dark:text-green-400" size={18} />,
    error: <AlertCircle className="text-red-500 dark:text-red-400" size={18} />,
    info: <Info className="text-[#0f7699] dark:text-[#4fc3f7]" size={18} />,
    warning: <AlertTriangle className="text-amber-500 dark:text-amber-400" size={18} />,
  };

  const styles = {
    success: 'bg-white dark:bg-slate-800 border-green-500/40 dark:border-green-400/40',
    error: 'bg-white dark:bg-slate-800 border-red-500/40 dark:border-red-400/40',
    info: 'bg-white dark:bg-slate-800 border-[#0f7699]/40 dark:border-[#4fc3f7]/40',
    warning: 'bg-white dark:bg-slate-800 border-amber-500/40 dark:border-amber-400/40',
  };

  const accentBar = {
    success: 'bg-green-500 dark:bg-green-400',
    error: 'bg-red-500 dark:bg-red-400',
    info: 'bg-[#0f7699] dark:bg-[#4fc3f7]',
    warning: 'bg-amber-500 dark:bg-amber-400',
  };

  return (
    <div
      className={`
        flex items-center gap-3 p-3 pr-10 rounded-xl border shadow-lg
        animate-in slide-in-from-top-2 sm:slide-in-from-right-full duration-300 mb-2 relative group overflow-hidden
        ${styles[type] || styles.info}
      `}
    >
      {/* Colored left accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${accentBar[type] || accentBar.info}`} />

      <div className="shrink-0 ml-1">
        {icons[type] || icons.info}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-tight">
          {message}
        </p>
      </div>
      <button
        onClick={() => onRemove(id)}
        className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
      >
        <X size={14} className="text-slate-400 dark:text-slate-500" />
      </button>

      {/* Progress bar for auto-dismiss */}
      <div
        className={`absolute bottom-0 left-0 h-0.5 ${accentBar[type] || accentBar.info} opacity-40 animate-progress-shrink w-full origin-left`}
        style={{ animationDuration: `${duration}ms` }}
      />
    </div>
  );
};

export const ConfirmModal = () => {
  const { confirmData, handleConfirm } = useNotification();

  if (!confirmData) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300"
        onClick={() => handleConfirm(false)}
      />
      <div className="bg-white dark:bg-slate-900 max-w-md w-full rounded-[2.5rem] shadow-2xl relative z-10 overflow-hidden animate-in zoom-in duration-300 border border-slate-200 dark:border-slate-700">
        <div className="bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] p-8 text-center space-y-4">
           <div className="w-16 h-16 bg-white/20 rounded-2xl mx-auto flex items-center justify-center text-white shadow-inner">
             <AlertCircle size={32} />
           </div>
           <h2 className="text-xl font-black text-white uppercase tracking-tight">{confirmData.title}</h2>
        </div>
        <div className="p-6 sm:p-10 space-y-6 sm:space-y-8">
          <p className="text-slate-600 dark:text-slate-300 font-bold leading-relaxed text-center">
            {confirmData.message}
          </p>
          <div className="grid grid-cols-2 gap-4">
             <button
               onClick={() => handleConfirm(false)}
               className="h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-black uppercase tracking-wider text-xs hover:bg-slate-200 dark:hover:bg-slate-700 transition-all active:scale-95 border border-slate-200 dark:border-slate-700"
             >
               {confirmData.cancelText}
             </button>
             <button
               onClick={() => handleConfirm(true)}
               className="h-14 rounded-2xl bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] text-white font-black uppercase tracking-wider text-xs shadow-lg shadow-[#0f7699]/20 hover:scale-105 active:scale-95 transition-all"
             >
               {confirmData.confirmText}
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NotificationContainer = () => {
  const { notifications, removeNotification } = useNotification();

  // Two stacks: default (bottom-right on desktop, top on mobile) and bottom-left
  // (bottom-left on desktop, hidden on mobile via `hideMobile`).
  const rightNotifs = notifications.filter((n) => n.position !== 'bottom-left');
  const leftNotifs = notifications.filter((n) => n.position === 'bottom-left');

  return (
    <>
      <ConfirmModal />
      {/* Default stack — Mobile: top, below header | Desktop: bottom-right */}
      <div className="fixed top-[4.5rem] left-3 right-3 sm:top-auto sm:left-auto sm:bottom-8 sm:right-8 z-[9999] flex flex-col sm:items-end sm:max-w-sm sm:w-full pointer-events-none">
        <div className="w-full pointer-events-auto">
          {rightNotifs.map((n) => (
            <NotificationItem
              key={n.id}
              {...n}
              onRemove={removeNotification}
              duration={n.duration}
            />
          ))}
        </div>
      </div>
      {/* Bottom-left stack — Desktop only; hidden on mobile (sm:flex). Used for
          editor placement hints and similar non-critical UX feedback. */}
      <div className="hidden sm:flex fixed sm:bottom-8 sm:left-8 sm:max-w-sm sm:w-full z-[9999] flex-col items-start pointer-events-none">
        <div className="w-full pointer-events-auto">
          {leftNotifs.map((n) => (
            <NotificationItem
              key={n.id}
              {...n}
              onRemove={removeNotification}
              duration={n.duration}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default NotificationContainer;
